import { createContext, useContext, useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { api } from '../services/apiService';
import {
  DeployBatch,
  DeployPlan,
  RepoProgress,
  RepoState,
  seedProgress,
  evaluatePhase,
  pendingReposInPhase,
  hasInFlight,
  isInFlight,
  statusToState,
  serializeBatch,
  deserializeBatch,
  STORAGE_KEY,
} from '../utils/deployAllOrchestration';

// Dispatch pacing — waves of 5, 12s apart, to stay under CFN / API Gateway rate
// caps that started failing when 10+ deploys were triggered together.
const BATCH_SIZE = 5;
const BATCH_DELAY_MS = 12000;
const POLL_INTERVAL_MS = 10000;

const newId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* ignore */ }
  return `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
};

interface OpenPreviewOpts {
  environment: 'dev' | 'prod';
  product: string;
  org: string;
}

interface DeployAllContextValue {
  batch: DeployBatch | null;
  isModalOpen: boolean;
  loadingPlan: boolean;
  openPreview: (opts: OpenPreviewOpts) => void;
  startBatch: () => void;
  cancelOne: (repoName: string) => void;
  skipOne: (repoName: string) => void;
  rerunOne: (repoName: string) => void;
  retryFailed: () => void;
  skipAndContinue: () => void;
  restart: () => void;
  minimize: () => void;
  expand: () => void;
  discard: () => void;
}

const DeployAllContext = createContext<DeployAllContextValue | null>(null);

export function DeployAllProvider({ children }: { children: ReactNode }) {
  const [batch, setBatch] = useState<DeployBatch | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);

  // Latest-batch ref for the async dispatch/poll loops (avoids stale closures).
  const batchRef = useRef<DeployBatch | null>(null);
  useEffect(() => { batchRef.current = batch; }, [batch]);

  const advancingRef = useRef(false);
  const rehydratedRef = useRef(false);

  // ── progress writers ───────────────────────────────────────────────────────
  const writeProgress = useCallback((
    repoName: string,
    next: RepoProgress | ((prev: RepoProgress) => RepoProgress),
  ) => {
    setBatch(b => {
      if (!b) return b;
      const prev = b.progress[repoName] || { state: 'pending' as RepoState };
      const val = typeof next === 'function' ? (next as (p: RepoProgress) => RepoProgress)(prev) : next;
      return { ...b, progress: { ...b.progress, [repoName]: val }, updatedAt: Date.now() };
    });
  }, []);

  // ── dispatch ───────────────────────────────────────────────────────────────
  const dispatchOne = useCallback(async (repoName: string) => {
    const b = batchRef.current;
    if (!b) return;
    const { org, product, environment } = b;
    const dispatchedAt = Date.now();
    writeProgress(repoName, {
      state: 'dispatching',
      startedAt: dispatchedAt,
      dispatchedAt,
      owner: org,
      runId: undefined,
      runUrl: undefined,
      finishedAt: undefined,
      message: undefined,
    });
    try {
      await api.devops.triggerDeploy(org, repoName, { product, environment });
      writeProgress(repoName, prev => ({ ...prev, state: 'queued' }));
    } catch (err: any) {
      writeProgress(repoName, {
        state: 'failure',
        startedAt: dispatchedAt,
        dispatchedAt,
        owner: org,
        finishedAt: Date.now(),
        message: err?.message || 'dispatch failed',
      });
    }
  }, [writeProgress]);

  const dispatchWaves = useCallback(async (repoNames: string[]) => {
    for (let i = 0; i < repoNames.length; i += BATCH_SIZE) {
      const wave = repoNames.slice(i, i + BATCH_SIZE);
      await Promise.allSettled(wave.map(r => dispatchOne(r)));
      if (i + BATCH_SIZE < repoNames.length) {
        await new Promise(resolve => setTimeout(resolve, BATCH_DELAY_MS));
      }
    }
  }, [dispatchOne]);

  const startPhase = useCallback(async (idx: number) => {
    const b = batchRef.current;
    if (!b?.plan) return;
    const ph = b.plan.phases[idx];
    if (!ph) return;
    setBatch(prev => (prev ? { ...prev, currentPhaseIdx: idx, updatedAt: Date.now() } : prev));
    // Mark deploy-locked repos skipped up front.
    for (const r of ph.repos) {
      if (r.deployLocked) {
        writeProgress(r.repoName, { state: 'skipped', startedAt: Date.now(), finishedAt: Date.now(), message: 'deploy locked' });
      }
    }
    const active = ph.repos.filter(r => !r.deployLocked).map(r => r.repoName);
    await dispatchWaves(active);
  }, [dispatchWaves, writeProgress]);

  // ── public actions ─────────────────────────────────────────────────────────
  const openPreview = useCallback(async ({ environment, product, org }: OpenPreviewOpts) => {
    const preview: DeployBatch = {
      id: newId(), environment, product, org,
      status: 'preview', currentPhaseIdx: -1, plan: null, progress: {},
      batchStartedAt: null, updatedAt: Date.now(),
    };
    batchRef.current = preview;
    setBatch(preview);
    setModalOpen(true);
    setLoadingPlan(true);
    try {
      const plan = await api.devops.getDeployPlan(environment, product) as DeployPlan;
      setBatch(b => (b && b.id === preview.id ? { ...b, plan } : b));
    } catch (err) {
      console.error('getDeployPlan error', err);
    } finally {
      setLoadingPlan(false);
    }
  }, []);

  const startBatch = useCallback(async () => {
    const b = batchRef.current;
    if (!b?.plan) return;
    const started: DeployBatch = {
      ...b, status: 'running', currentPhaseIdx: -1,
      progress: seedProgress(b.plan), batchStartedAt: Date.now(), updatedAt: Date.now(),
    };
    batchRef.current = started;
    setBatch(started);
    setModalOpen(true);
    await startPhase(0);
  }, [startPhase]);

  const cancelOne = useCallback(async (repoName: string) => {
    const b = batchRef.current;
    if (!b) return;
    const p = b.progress[repoName];
    const owner = p?.owner || b.org;
    const runId = p?.runId;
    // Optimistic: cancelled is terminal + blocking → phase halts for user decision.
    writeProgress(repoName, prev => ({ ...prev, state: 'cancelled', finishedAt: Date.now(), message: 'cancel requested' }));
    if (runId) {
      try {
        await api.devops.cancelRun(owner, repoName, runId);
      } catch (err: any) {
        writeProgress(repoName, prev => ({ ...prev, message: `cancel failed: ${err?.message || ''}` }));
      }
    }
  }, [writeProgress]);

  const skipOne = useCallback((repoName: string) => {
    writeProgress(repoName, prev => ({ ...prev, state: 'skipped', finishedAt: prev.finishedAt || Date.now(), message: 'skipped' }));
    // If we'd halted on this repo, resume so the advance effect can re-evaluate.
    setBatch(b => (b && b.status === 'failed' ? { ...b, status: 'running', updatedAt: Date.now() } : b));
  }, [writeProgress]);

  const rerunOne = useCallback(async (repoName: string) => {
    // From a halted phase, resume; from a fully terminal batch, leave the batch
    // status alone — the poll loop tracks this single re-dispatched run via its
    // in-flight clause without re-driving phase progression.
    setBatch(b => (b && b.status === 'failed' ? { ...b, status: 'running', updatedAt: Date.now() } : b));
    await dispatchOne(repoName);
  }, [dispatchOne]);

  const retryFailed = useCallback(async () => {
    const b = batchRef.current;
    if (!b?.plan) return;
    const ph = b.plan.phases[b.currentPhaseIdx];
    if (!ph) return;
    const failed = ph.repos.filter(r => b.progress[r.repoName]?.state === 'failure').map(r => r.repoName);
    if (!failed.length) return;
    setBatch(x => (x ? { ...x, status: 'running', updatedAt: Date.now() } : x));
    await dispatchWaves(failed);
  }, [dispatchWaves]);

  const skipAndContinue = useCallback(() => {
    const b = batchRef.current;
    if (!b?.plan) return;
    const ph = b.plan.phases[b.currentPhaseIdx];
    if (!ph) return;
    setBatch(x => {
      if (!x) return x;
      const next = { ...x.progress };
      for (const r of ph.repos) {
        const st = next[r.repoName]?.state;
        if (st === 'failure' || st === 'cancelled') next[r.repoName] = { ...next[r.repoName], state: 'skipped' };
      }
      return { ...x, progress: next, status: 'running', updatedAt: Date.now() };
    });
  }, []);

  const restart = useCallback(async () => {
    const b = batchRef.current;
    if (!b) return;
    setLoadingPlan(true);
    let plan = b.plan;
    try {
      plan = await api.devops.getDeployPlan(b.environment, b.product) as DeployPlan;
    } catch (err) {
      console.error('restart getDeployPlan error', err);
    } finally {
      setLoadingPlan(false);
    }
    const restarted: DeployBatch = {
      ...b, id: newId(), plan, status: 'running', currentPhaseIdx: -1,
      progress: seedProgress(plan), batchStartedAt: Date.now(), updatedAt: Date.now(),
    };
    batchRef.current = restarted;
    setBatch(restarted);
    setModalOpen(true);
    await startPhase(0);
  }, [startPhase]);

  const minimize = useCallback(() => setModalOpen(false), []);
  const expand = useCallback(() => setModalOpen(true), []);
  const discard = useCallback(() => {
    batchRef.current = null;
    setBatch(null);
    setModalOpen(false);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  const resumeCurrentPhase = useCallback(async () => {
    const b = batchRef.current;
    if (!b || b.status !== 'running') return;
    const ph = b.plan?.phases[b.currentPhaseIdx];
    const pending = pendingReposInPhase(ph, b.progress);
    if (pending.length) await dispatchWaves(pending);
  }, [dispatchWaves]);

  // ── rehydrate on mount ───────────────────────────────────────────────────────
  useEffect(() => {
    if (rehydratedRef.current) return;
    rehydratedRef.current = true;
    let restored: DeployBatch | null = null;
    try { restored = deserializeBatch(localStorage.getItem(STORAGE_KEY)); } catch { /* ignore */ }
    if (restored) {
      batchRef.current = restored;
      setBatch(restored);
      setModalOpen(false); // resume minimized so the widget shows
      // Let state commit, then dispatch any pending repos in the current phase.
      setTimeout(() => { void resumeCurrentPhase(); }, 0);
    }
  }, [resumeCurrentPhase]);

  // ── persist (running batches only — they're the only resumable ones) ─────────
  useEffect(() => {
    try {
      if (batch && batch.status === 'running') localStorage.setItem(STORAGE_KEY, serializeBatch(batch));
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* quota / private mode — non-fatal */ }
  }, [batch]);

  // ── phase advancement ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!batch || batch.status !== 'running' || batch.currentPhaseIdx < 0 || advancingRef.current) return;
    const ph = batch.plan?.phases[batch.currentPhaseIdx];
    const outcome = evaluatePhase(ph, batch.progress);
    if (outcome.kind === 'in-progress') return;
    if (outcome.kind === 'blocked') {
      setBatch(b => (b ? { ...b, status: 'failed', updatedAt: Date.now() } : b));
      return;
    }
    // complete
    if (batch.currentPhaseIdx + 1 >= (batch.plan?.phases.length || 0)) {
      setBatch(b => (b ? { ...b, status: 'done', updatedAt: Date.now() } : b));
      return;
    }
    advancingRef.current = true;
    (async () => {
      await startPhase(batch.currentPhaseIdx + 1);
      advancingRef.current = false;
    })();
  }, [batch, startPhase]);

  // ── poll GitHub status (active while running OR any repo in-flight) ───────────
  const pollActive = !!batch && (batch.status === 'running' || hasInFlight(batch.progress));
  useEffect(() => {
    if (!pollActive) return;
    const run = async () => {
      const b = batchRef.current;
      if (!b) return;
      try {
        const data: any = await api.devops.listReposSilent({ product: b.product });
        const repos: any[] = data?.repos || [];
        setBatch(prev => {
          if (!prev) return prev;
          const next = { ...prev.progress };
          let changed = false;
          for (const repoName of Object.keys(next)) {
            const p = next[repoName];
            if (!isInFlight(p.state)) continue; // only reconcile in-flight repos
            const r = repos.find(x => x.repoName === repoName);
            if (!r) continue;
            const envKey = prev.environment;
            const runStatus = r[`${envKey}RunStatus`] || r[`${envKey}Status`];
            const lastRun = r[`last${envKey === 'dev' ? 'Dev' : 'Prod'}Run`];
            const newState = statusToState(runStatus, lastRun?.conclusion);
            if (!newState) continue;
            const backendRunId = lastRun?.id;
            const runStartedAt = lastRun?.run_started_at ? new Date(lastRun.run_started_at).getTime() : 0;
            const idAdvanced = !p.runId || (backendRunId && backendRunId >= p.runId);
            const dispatchOk = !p.dispatchedAt || (runStartedAt >= p.dispatchedAt - 30_000);
            if (newState !== p.state && idAdvanced && dispatchOk) {
              const terminal = newState === 'success' || newState === 'failure' || newState === 'cancelled';
              next[repoName] = {
                ...p,
                state: newState,
                runId: backendRunId || p.runId,
                runUrl: lastRun?.html_url || p.runUrl,
                startedAt: p.startedAt || runStartedAt || Date.now(),
                finishedAt: terminal ? Date.now() : p.finishedAt,
              };
              changed = true;
            }
          }
          return changed ? { ...prev, progress: next, updatedAt: Date.now() } : prev;
        });
      } catch (err) {
        console.warn('deploy-all poll error', err);
      }
    };
    run();
    const id = setInterval(run, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [pollActive]);

  const value: DeployAllContextValue = {
    batch, isModalOpen, loadingPlan,
    openPreview, startBatch, cancelOne, skipOne, rerunOne,
    retryFailed, skipAndContinue, restart, minimize, expand, discard,
  };

  return <DeployAllContext.Provider value={value}>{children}</DeployAllContext.Provider>;
}

export function useDeployAll(): DeployAllContextValue {
  const ctx = useContext(DeployAllContext);
  if (!ctx) throw new Error('useDeployAll must be used within DeployAllProvider');
  return ctx;
}
